/**
 * Move notes captured outside the app into the offline capture queue.
 *
 * Two steps, so nothing is lost if the app dies in between: copy each note
 * into the queue, then remove it from the native store. If the removal never
 * happens, the next import copies it again under the same `client_id`, which
 * overwrites the queued copy, and the API ignores a replay of a synced one.
 */
import type { NativeNote } from '@/native/captureInbox'
import * as queue from './queue'

export interface NativeInboxDeps {
  list: () => Promise<NativeNote[]>
  remove: (clientIds: string[]) => Promise<void>
  store: queue.QueueDeps['store']
}

export async function importNativeNotes(deps: NativeInboxDeps, userId: string): Promise<number> {
  // Only this rider's notes. Another rider's wait until they sign in again.
  const notes = (await deps.list()).filter((n) => n.user_id === userId)
  const imported: string[] = []
  for (const n of notes) {
    const text = n.text.trim()
    if (!text) {
      // Nothing to keep; clear it so it does not come back.
      imported.push(n.client_id)
      continue
    }
    await queue.enqueue(deps, userId, {
      client_id: n.client_id,
      captured_at: n.captured_at,
      text,
      bike_id: n.bike_id,
    })
    imported.push(n.client_id)
  }
  await deps.remove(imported)
  return notes.filter((n) => n.text.trim()).length
}
